import { useEffect, useRef } from "react";
import { APIProvider, Map, useMap } from "@vis.gl/react-google-maps";

const EMPTY_POLYGON = [];

function PolygonDrawer({ initialPolygon = [], onPolygonChange, clearTrigger }) {
  const map = useMap();

  const polygonRef = useRef(null);
  const markersRef = useRef([]);
  const pointsRef = useRef([]);
  const initialPolygonKeyRef = useRef("");

  useEffect(() => {
    if (!map || !window.google) return;

    polygonRef.current = new window.google.maps.Polygon({
      paths: [],
      strokeColor: "#1976d2",
      strokeOpacity: 1,
      strokeWeight: 2,
      fillColor: "#1976d2",
      fillOpacity: 0.3,
      editable: false,
      clickable: false,
      zIndex: 1,
      map,
    });

    function redrawPolygon() {
      if (pointsRef.current.length < 3) {
        polygonRef.current.setPath([]);
      } else {
        polygonRef.current.setPath(
          pointsRef.current.map((point) => ({
            lat: point.latitude,
            lng: point.longitude,
          })),
        );
      }

      onPolygonChange?.([...pointsRef.current]);
    }

    function createMarker(point) {
      const marker = new window.google.maps.Marker({
        position: {
          lat: point.latitude,
          lng: point.longitude,
        },
        draggable: true,
        zIndex: 10,
        map,
      });

      function getIndex() {
        return markersRef.current.indexOf(marker);
      }

      marker.addListener("drag", (event) => {
        const index = getIndex();

        if (index === -1) return;

        pointsRef.current[index] = {
          latitude: event.latLng.lat(),
          longitude: event.latLng.lng(),
        };

        redrawPolygon();
      });

      marker.addListener("rightclick", () => {
        const index = getIndex();

        if (index === -1) return;

        marker.setMap(null);

        markersRef.current.splice(index, 1);
        pointsRef.current.splice(index, 1);

        redrawPolygon();
      });

      return marker;
    }

    function clearMarkers() {
      markersRef.current.forEach((marker) => {
        marker.setMap(null);
      });

      markersRef.current = [];
      pointsRef.current = [];
    }

    function loadInitialPolygon() {
      const initialPolygonKey = JSON.stringify(initialPolygon);

      if (
        initialPolygonKeyRef.current === initialPolygonKey ||
        !Array.isArray(initialPolygon) ||
        initialPolygon.length === 0
      ) {
        return;
      }

      clearMarkers();

      pointsRef.current = initialPolygon.filter(
        (point) =>
          typeof point.latitude === "number" &&
          typeof point.longitude === "number",
      );

      markersRef.current = pointsRef.current.map((point) =>
        createMarker(point),
      );

      if (pointsRef.current.length > 0) {
        const bounds = new window.google.maps.LatLngBounds();

        pointsRef.current.forEach((point) => {
          bounds.extend({
            lat: point.latitude,
            lng: point.longitude,
          });
        });

        map.fitBounds(bounds, 80);
      }

      initialPolygonKeyRef.current = initialPolygonKey;
      redrawPolygon();
    }

    const clickListener = map.addListener("click", (event) => {
      const point = {
        latitude: event.latLng.lat(),
        longitude: event.latLng.lng(),
      };

      pointsRef.current.push(point);

      const marker = createMarker(point);

      markersRef.current.push(marker);

      redrawPolygon();
    });

    loadInitialPolygon();

    return () => {
      window.google.maps.event.removeListener(clickListener);

      markersRef.current.forEach((marker) => marker.setMap(null));
      markersRef.current = [];
      pointsRef.current = [];
      initialPolygonKeyRef.current = "";

      polygonRef.current?.setMap(null);
      polygonRef.current = null;
    };
  }, [initialPolygon, map, onPolygonChange]);

  useEffect(() => {
    if (!clearTrigger) return;

    markersRef.current.forEach((marker) => {
      marker.setMap(null);
    });

    markersRef.current = [];
    pointsRef.current = [];

    if (polygonRef.current) {
      polygonRef.current.setPath([]);
    }

    onPolygonChange?.([]);
  }, [clearTrigger, onPolygonChange]);

  return null;
}

export default function GoogleMapView({
  initialPolygon = EMPTY_POLYGON,
  onPolygonChange,
  clearTrigger,
}) {
  return (
    <APIProvider
      apiKey={"AIzaSyBIbtiiqiEJ6F2ScRafjfv1SmpaeWg3NDQ"}
      libraries={["geometry", "places"]}
    >
      <Map
        style={{
          width: "100%",
          height: "100%",
        }}
        defaultCenter={{
          lat: -16.409047,
          lng: -71.537451,
        }}
        defaultZoom={13}
        gestureHandling="greedy"
      >
        <PolygonDrawer
          initialPolygon={initialPolygon}
          onPolygonChange={onPolygonChange}
          clearTrigger={clearTrigger}
        />
      </Map>
    </APIProvider>
  );
}
